import { useQueries, useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import icons from "../../../../../assets/images/icons";
import { getUserBlockStatus } from "../../../apis/chatApi";
import { blockedUsersQueryKey } from "../../../hooks/useChatBlockUsersLoad";
import { useChatRoomsLoad } from "../../../hooks/useChatRoomsLoad";
import ChatHeader from "../../shared/ChatHeader/ChatHeader";
import ConfirmModal from "../../shared/ConfirmModal/ConfirmModal";
import Toast from "../../shared/Toast/Toast";
import MessageList from "../MessageList/MessageList";
import styles from "./ChatRoomListPage.module.scss";

const LIST_TITLE = "채팅";
const LIST_DELETE_TITLE = "채팅삭제";
const EMPTY_TITLE = "채팅 내역이 없습니다.";
const EMPTY_SUBTITLE = "원하시는 매물 정보 중개사에게 문의해보세요!";

function ChatRoomListPage({
  className = "",
  onSettingClick,
  onCloseClick,
  onRoomClick,
  onLeaveRooms,
  deleteMode = false,
  onSetDeleteMode,
  toastMessage,
}) {
  const { data: rooms = [], isSuccess } = useChatRoomsLoad();

  const { data: blockedUsers = [] } = useQuery({
    queryKey: blockedUsersQueryKey,
    enabled: false,
  });
  const blockedUserIdSet = useMemo(
    () => new Set(blockedUsers.map((u) => u.user_id)),
    [blockedUsers]
  );
  const myUserId = window.loggedInUser?.uuid;

  // 각 방의 상대방 ID 수집 (중복 제거). "상대가 나를 차단했는지" 판별용.
  const otherUserIds = useMemo(() => {
    const ids = rooms
      .map((room) => room.members?.find((m) => m.userId !== myUserId)?.userId)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [rooms, myUserId]);

  // 상대가 나를 차단한 상태 — 추후 백엔드에서 나를 차단한 유저 목록을 불러오는게 더 좋긴함
  const blockStatusQueries = useQueries({
    queries: otherUserIds.map((userId) => ({
      queryKey: ["blockStatus", userId],
      queryFn: () => getUserBlockStatus(userId),
      staleTime: Infinity,
      enabled: !!userId,
    })),
  });

  const blockedByOtherSet = useMemo(() => {
    const set = new Set();
    otherUserIds.forEach((userId, i) => {
      if (blockStatusQueries[i]?.data?.blockedByOther) set.add(userId);
    });
    return set;
  }, [otherUserIds, blockStatusQueries]);

  const items = useMemo(
    () =>
      rooms.map((room) => {
        const otherUserId = room.members?.find((m) => m.userId !== myUserId)?.userId;
        return {
          ...room,
          banaccount: blockedUserIdSet.has(otherUserId),
          banaccountByOther: blockedByOtherSet.has(otherUserId),
        };
      }),
    [rooms, blockedUserIdSet, blockedByOtherSet, myUserId]
  );

  const hasRooms = items.length > 0;
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const selectedCount = selectedRoomIds.length;
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  useEffect(() => {
    if (!deleteMode) setSelectedRoomIds([]);
  }, [deleteMode]);

  const exitDeleteMode = useCallback(() => {
    setSelectedRoomIds([]);
    onSetDeleteMode?.(false);
  }, [onSetDeleteMode]);

  const handleConfirmLeave = async () => {
    setLeaveModalOpen(false);
    const ok = await onLeaveRooms?.(selectedRoomIds);
    if (ok) {
      setSelectedRoomIds([]);
      onSetDeleteMode?.(false);
    }
  };

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-name="Mobile"
      data-node-id={
        deleteMode && hasRooms ? "40004311:86172" : hasRooms ? "40004311:83161" : "40004311:79999"
      }
    >
      <div
        className={styles.panel}
        data-name="Messages Panel"
        data-node-id={
          deleteMode && hasRooms ? "40004311:86173" : hasRooms ? "40004311:83162" : "40004311:80000"
        }
      >
        <ChatHeader
          title={deleteMode ? LIST_DELETE_TITLE : LIST_TITLE}
          type={deleteMode ? "delete" : "main"}
          showTrash={hasRooms}
          onTrashClick={() => onSetDeleteMode?.(true)}
          onSettingClick={onSettingClick}
          onCloseClick={onCloseClick}
          onCancelDelete={exitDeleteMode}
          onConfirmDelete={() => setLeaveModalOpen(true)}
          onBackClick={exitDeleteMode}
        />
        <div className={styles.divider} data-node-id="40004311:80027" />
        <div
          className={`${styles.messageArea} ${hasRooms ? styles.hasRooms : ""}`.trim()}
          data-name="Message List"
          data-node-id={deleteMode && hasRooms ? "40004311:86207" : "40004311:80028"}
        >
          {!isSuccess ? null : hasRooms ? (
            <div className={styles.listWrap}>
              <MessageList
                items={items}
                onItemClick={deleteMode ? undefined : onRoomClick}
                editMode={deleteMode}
                selectedIds={selectedRoomIds}
                onSelectionChange={setSelectedRoomIds}
              />
            </div>
          ) : (
            <div className={styles.emptyState} data-node-id="40004311:80029">
              <div className={styles.emptyEmoji} data-node-id="40004311:80030" aria-hidden>
                <img
                  src={icons.messager.emptyState}
                  alt=""
                  className={styles.emptyEmojiImg}
                  width={64}
                  height={64}
                />
              </div>
              <div className={styles.emptyText} data-node-id="40004311:80053">
                <p className={styles.emptyTitle} data-node-id="40004311:80054">
                  {EMPTY_TITLE}
                </p>
                <p className={styles.emptySubtitle} data-node-id="40004311:80055">
                  {EMPTY_SUBTITLE}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {deleteMode && (
        <div
          className={`${styles.deleteBar} ${selectedCount === 0 ? styles.deleteBarDisabled : ""}`.trim()}
          data-name="┗ Main Action"
          data-node-id={selectedCount === 0 ? "40004311:85087" : "40004311:86208"}
        >
          <button
            type="button"
            className={styles.deleteBarBtn}
            disabled={selectedCount === 0}
            onClick={() => setLeaveModalOpen(true)}
          >
            <span className={styles.deleteBarLabel}>
              {selectedCount > 0 ? `${selectedCount}개 채팅방 나가기` : "0개 채팅방 나가기"}
            </span>
            {icons.messager?.leave && (
              <img
                src={icons.messager.leave}
                alt=""
                className={styles.deleteBarIcon}
                width={24}
                height={24}
                aria-hidden
              />
            )}
          </button>
        </div>
      )}
      <ConfirmModal
        type="leave"
        open={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        onConfirm={handleConfirmLeave}
      />
      <Toast message={toastMessage} data-node-id="40004313:56206" />
    </div>
  );
}

export default ChatRoomListPage;
